import {SECTION_ANY_ID, BookingMethods} from "shared-types/index";
import ObjectCopyService from "external-services/object-copy-service/index";
import GeneralSettingsService from "external-services/general-settings-service/generalSettings.service";
import AdminService from "external-services/diary-admin-service/admin.service";
import MenuOptionsService from "app/services/menuOptions/menuOptions.service";
import {cloneDeep} from "lodash-es";
import PaymentService from "external-services/diary-payment-service";
import SectionLookupService from "external-services/section-lookup-service/sectionLookup.service";
import BookingIntervalService from "app/services/bookingInterval/bookingInterval.service";
import MaxOccupancyService, {OVER_MAX} from "app/services/maxOccupancy/maxOccupancy.service";
import BookingLookupService from "external-services/booking-lookup-service/index";
import BookingUpdateService from "external-services/booking-update-service/index";

const NS = 'PeopleEditor'; // jshint ignore:line

// requiring html templates so that hot reloading works
const template = require('./peopleEditor.html');

export default function PeopleEditor(
  $mdDialog
) {
  "ngInject";

  return {
    replace: true,
    bindToController: true,
    controller: function () {
    },
    controllerAs: 'vm',
    template,
    restrict: 'E',
    link,
    scope: {
      /**
       * options:
       * position: { top: number, left: number };
       * booking: IBooking;
       * bookingIntervals: IScheduleInterval[];
       * schedule: ISchedule
       */
      options: '=',

      /**
       * boolean
       */
      open: '='
    }
  }

  function link(scope) {

    const vm = scope.vm;
    let settings;
    let section;
    let coversBookingIntervals;
    let maxOccupancyEnabled = false;
    vm.disableSave = false;
    vm.up = up;
    vm.down = down;
    vm.save = save;
    vm.close = close;

    let orgBooking = ObjectCopyService.angularSafeCopy(vm.options.booking);

    GeneralSettingsService.get()
      .then(s => {
        maxOccupancyEnabled = s && s.enableMaxOccupancy;
        settings = s;

        if (maxOccupancyEnabled) {
          initMaxOccupancy();
        }
      });

    // check couch url/name still correct
    AdminService.restartPeriodicCouchNameCheck(); // restarts the timer
    AdminService.loadVenue(AdminService.getVenue().id, true, true).catch(function (err) {
      console.error(err);
    });

    /**
     * Loads data needed for Max Occupancy calculations
     */
    function initMaxOccupancy() {
      SectionLookupService.getSectionsLookUp()
        .then((sections) => {
          section = sections[getSectionId(orgBooking)] || null;
          vm.sections = [section];
          showMaxOccupancyMessage();
        });

        BookingLookupService.getBySchedule(vm.options.schedule)
        .then(({bookings}) => {

          /**
           * `bookingsInSection` gets all bookings in the same section.
           * Includes all services for the day, but `bookingIntervals` is filtered to the current service, so it
           * narrows down the list in `BookingIntervalService.getCoversPerScheduleInterval`.
           */
          const bookingsInSection = vm.options.bookings.filter(booking => {
            return booking._id !== orgBooking._id && SectionLookupService.checkIfSectionExists(booking, orgBooking.preferredSectionId);
          });

          coversBookingIntervals = BookingIntervalService.getCoversPerMinuteInterval(vm.options.bookingIntervals, bookingsInSection, vm.sections);

          showMaxOccupancyMessage();
        });
    }

    function getSectionId(booking) {
      const primaryTable = booking.tables?.find(t => t._id === booking.primaryTableId);
      if (primaryTable) {
        return primaryTable.sectionId;
      } else if (booking.preferredSectionId === SECTION_ANY_ID && booking.tables && booking.tables.length) {
        return booking.tables[0].sectionId;
      }
      return booking.preferredSectionId;
    }

    function up() {
      vm.options.booking.people++;
      showMaxOccupancyMessage();
      showPaxThresholdError();
    }

    function down() {
      if (vm.options.booking.people > 1) {
        vm.options.booking.people--;
        showMaxOccupancyMessage();
        showPaxThresholdError();
      }
    }

    function showPaxThresholdError() {
      const service = vm.options.schedule?.services.find(({_id}) => vm.options.booking.serviceId === _id);
      const bookingCopy = cloneDeep(vm.options.booking);
      const activePaymentDetails = PaymentService.setActivePayments(service.paymentDetails, service.additionalPaymentDetails,
        bookingCopy.people, bookingCopy.time);

      vm.disableSave = (vm.options.booking.selectedMenuOptions?.length > 0) &&
        (MenuOptionsService.checkIfOptionsChanged(activePaymentDetails?.menuOptions, vm.options.originalActivePaymentDetails.originalBOIds) ||
          MenuOptionsService.checkIfOptionsChanged(activePaymentDetails?.menuOptionsUpsell, vm.options.originalActivePaymentDetails.originalUOIds));

    }

    function showMaxOccupancyMessage() {
      if (!maxOccupancyEnabled || !section) {
        return;
      }

      vm.isMultipleSection = BookingIntervalService.checkIfBookingOverMultipleSections(vm.options.booking);
      const maxOccCssClassName = MaxOccupancyService.checkForCovidMaxOccupancy(vm.options.booking, coversBookingIntervals, [section], vm.isMultipleSection);
      vm.showMaxOccupancyMessage = maxOccCssClassName === OVER_MAX;
    }

    function close() {
      vm.open = false;
    }

    function save(event, message) {
      // this is to check if the new staff edit options are not enabled but the original staff edit are checked true
      const isOldEditSettings = settings.staffEditEnabled && !settings.staffEdit;
      const isNewEditSettings = settings.staffEdit && settings.staffEdit.staffEditQuickEdit;
      if ((isOldEditSettings || isNewEditSettings) && settings.staffEditEnabled && orgBooking.method !== BookingMethods.walkin) {
        vm.open = false;
        const staffNameDialog = $mdDialog.prompt()
          .title('Booking change')
          .placeholder('Staff name')
          .ariaLabel('Staff name')
          .targetEvent(event)
          .ok('Confirm')
          .cancel('Cancel')
          .clickOutsideToClose(true);

        if (message) {
          staffNameDialog.textContent(message);
        }

        $mdDialog.show(staffNameDialog).then(function (staff) {
          // required check is here because AngularJS Material 1.0.7 doesn't have a 'required' stetting for prompt dialogs
          if (settings.staffEditRequired && !staff) {
            save(event, 'Staff name required');
          } else {
            update(staff);
          }
        });
      } else {
        update();
        vm.open = false;
      }
    }

    function update(staff) {
      BookingUpdateService.updateFromAnywhere(vm.options.booking, orgBooking, null, null, staff);
      orgBooking = ObjectCopyService.angularSafeCopy(vm.options.booking);
    }
  }
}

